import {
  Box,
  DialogActions,
  DialogContent,
  Slider,
  Typography,
} from '@material-ui/core';
import { Cancel, Done, Edit } from '@material-ui/icons';
import { updateCoverImage, updateProfileImage } from '@webapps/business';
import {
  Button,
  useAuth,
  useMutation,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';

import getCroppedImg from './utils/CropImage';

const CropEasy = ({ photoURL, refresh, setOpenCrop, type, data }) => {
  const {
    css,
    classes,
    cx,
    global,
    theme: { spacing },
  } = useStyles();

  const { idUser } = useAuth();
  const { addToast } = useToast();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [photo, setPhoto] = useState(photoURL);
  const [file, setFile] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const cropComplete = useCallback((croppedArea, croppedAreaPixel) => {
    setCroppedAreaPixels(croppedAreaPixel);
  }, []);

  const handleChange = (e) => {
    const newFile = e.target.files[0];
    if (newFile) {
      setFile(newFile);
      setPhoto(URL.createObjectURL(newFile));
    }
  };

  const {
    error: errorCover,
    isLoading: isLoadingCover,
    isSuccess: isSuccessCover,
    mutateAsync: mutateUpdateCoverImage,
  } = useMutation(updateCoverImage);

  const {
    error: errorProfile,
    isLoading: isLoadingProfile,
    isSuccess: isSuccessProfile,
    mutateAsync: mutateUpdateProfileImage,
  } = useMutation(updateProfileImage);

  const zoomPercent = (value) => `${Math.round(value * 100)}%`;

  const cropImage = async () => {
    try {
      const { file: fileToUpload } = await getCroppedImg(
        photo,
        croppedAreaPixels,
        rotation
      );
      const upl = new File([fileToUpload], 'name', { type: fileToUpload.type });
      if (type === 'cover') {
        await mutateUpdateCoverImage({
          data: upl,
          id: idUser,
        });
      } else if (type === 'profile') {
        await mutateUpdateProfileImage({
          data: upl,
          id: idUser,
        });
      } else if (type === 'businessExternal') {
        data.picture = upl;
        addToast({
          status: 'success',
          message: 'Photo ajoutée avec succès',
        });
        setOpenCrop(false);
      }
    } catch (errorRequest) {
      addToast({
        status: 'error',
        message: `Erreur pour mettre à jour la photo ${errorRequest}`,
      });
    }
  };

  useEffect(() => {
    if (errorCover) {
      addToast({
        status: 'error',
        message: 'Erreur pour mettre à jour la photo de couverture',
      });
    }
    if (isSuccessCover) {
      addToast({
        status: 'success',
        message: 'Photo de couverture mise à jour',
      });
      refresh();
      setOpenCrop(false);
    }
    if (errorProfile) {
      addToast({
        status: 'error',
        message: 'Erreur pour mettre à jour la photo de profil',
      });
    }
    if (isSuccessProfile) {
      addToast({
        status: 'success',
        message: 'Photo de profil mise à jour',
      });
      refresh();
      setOpenCrop(false);
    }
  }, [errorCover, isSuccessCover, errorProfile, isSuccessProfile]);

  return (
    <>
      <DialogContent
        className={css({
          background: '#333',
          height: 400,
          width: '100%',
          // minWidth: 500,
          position: 'relative',
        })}
        dividers
      >
        <div>
          <Cropper
            aspect={type === "cover" ? 1000 / 220 : 1}
            crop={crop}
            cropShape={(type === 'profile' || type === 'businessExternal') ? 'round' : 'rect'}
            image={photo}
            onCropChange={setCrop}
            onCropComplete={cropComplete}
            onRotationChange={setRotation}
            onZoomChange={setZoom}
            rotation={rotation}
            zoom={zoom}
          />
        </div>
      </DialogContent>
      <DialogActions className={css({ flexDirection: 'column' })}>
        <Box className={css({ width: '100%', padding: spacing(2) })}>
          <Box>
            <Typography
              className={css({ color: '#5D75A8', marginBottom: spacing(1) })}
            >
              Dimensions conseillées:{' '}
              {type === 'cover' ? '1584x386' : '300x300'}
            </Typography>
          </Box>
          <Box>
            <Typography className={global.title} >Zoom: {zoomPercent(zoom)}</Typography>
            <Slider
              max={3}
              min={1}
              onChange={(e, newZoom) => setZoom(newZoom)}
              step={0.1}
              value={zoom}
              valueLabelDisplay="auto"
              valueLabelFormat={zoomPercent}
            />
          </Box>
          <Box>
            <Typography className={global.title} >Rotation: {rotation}</Typography>
            <Slider
              max={360}
              min={0}
              onChange={(e, newRotation) => setRotation(newRotation)}
              value={rotation}
              valueLabelDisplay="auto"
            />
          </Box>
          <Box
            className={css({
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: spacing(1),
            })}
          >
            <Button
              onClick={() => setOpenCrop(false)}
              startIcon={<Cancel />}
              variant="outlined"
              style={{ borderRadius: 400, height: '100%', '@media only screen and (max-width:1920px)': {fontSize: '15.00px'}, '@media only screen and (max-width:1600px)': {fontSize: '14.00px'}, fontWeight: 600, }}
            >
              Annuler
            </Button>
            <label htmlFor="upload-photo">
              <input
                accept="image/png, image/jpg, image/jpeg"
                id="upload-photo"
                name="upload-photo"
                onChange={(e) => handleChange(e)}
                style={{ display: 'none' }}
                type="file"
              />
              <Button component="span" startIcon={<Edit />} variant="outlined" style={{ height: '100%', borderRadius: 400, '@media only screen and (max-width:1920px)': {fontSize: '15.00px'}, '@media only screen and (max-width:1600px)': {fontSize: '14.00px'}, fontWeight: 600, }}>
                Choisir une photo
              </Button>
            </label>
            <Button
              color="primary"
              disabled={isLoadingCover || isLoadingProfile || !photo}
              onClick={cropImage}
              startIcon={<Done />}
              variant="contained"
              style={{ borderRadius: 10, height: '100%', '@media only screen and (max-width:1920px)': {fontSize: '15.00px'}, '@media only screen and (max-width:1600px)': {fontSize: '14.00px'}, fontWeight: 600, }}
            >
              Enregistrer
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </>
  );
};

CropEasy.propTypes = {
  photoURL: PropTypes.string,
  type: PropTypes.string,
  setOpenCrop: PropTypes.func,
  refresh: PropTypes.func,
};

export default CropEasy;
